import ToolTipPopOver from '@atoms/ToolTipPopOver';
import { scale } from '@utils/normalize';
import React, { useRef, useState } from 'react';
import {
  Pressable,
  View,
  Modal,
  Animated,
  TouchableWithoutFeedback,
  Dimensions,
  UIManager,
  Platform,
} from 'react-native';
import logCustomTooltip from './analyticsCustomTooltip';
import styles from './styles';

const ControlledTooltip = ({ translateString = '', children, logParams = {} }) => {
  const [visible, setVisible] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const [top, setTop] = useState(0);

  /** 👉 This ref will point to the DOM node on web */
  const childRef = useRef<any>(null);

  /** STORE ONLAYOUT (fallback for native) */
  const childLayout = useRef<{ y: number; height: number } | null>(null);

  const handleLayout = (e: any) => {
    childLayout.current = e.nativeEvent.layout;
  };

  const openTooltip = () => {
    const refNode = childRef.current;
    if (!refNode) return;

    /** -------------------- WEB MEASUREMENT -------------------- **/
    if (Platform.OS === 'web') {
      let domNode = refNode;

      // unwrap react-native-web internal nodes
      if (domNode && domNode._node) domNode = domNode._node;

      if (domNode && domNode.getBoundingClientRect) {
        const rect = domNode.getBoundingClientRect();

        const absoluteTop = rect.top + window.scrollY;
        const h = rect.height;

        logCustomTooltip(logParams);
        setTop(absoluteTop + h + 4);

        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();

        setVisible(true);
        return;
      }

      console.warn('Tooltip: DOM node not found. Did you forward the ref?');
      return;
    }

    /** -------------------- NATIVE: measureInWindow -------------------- **/
    const node = childRef.current;

    if (node) {
      const handle = node._nativeTag; // safe for native

      UIManager.measureInWindow(handle, (_x, y, _w, h) => {
        logCustomTooltip(logParams);
        setTop(y + h + 4);

        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();

        setVisible(true);
      });
    }
  };

  const closeTooltip = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 150,
      useNativeDriver: true,
    }).start(() => setVisible(false));
  };

  const screenWidth = Dimensions.get('window').width;
  const tooltipWidth = screenWidth - scale(24);
  const left = (screenWidth - tooltipWidth) / 2;

  const animatedStyle = {
    position: 'absolute' as const,
    top,
    left,
    width: tooltipWidth,
    opacity: fadeAnim,
  };

  return (
    <>
      <Pressable ref={childRef} onPress={openTooltip} onLayout={handleLayout}>
        {children}
      </Pressable>

      {visible && (
        <Modal transparent animationType="none" onRequestClose={closeTooltip}>
          <TouchableWithoutFeedback onPress={closeTooltip}>
            <View style={styles.fullscreenOverlay}>
              <Animated.View style={[styles.tooltipContainerStyle, animatedStyle]}>
                <ToolTipPopOver translateString={translateString} setOpen={setVisible} />
              </Animated.View>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </>
  );
};

export default React.memo(ControlledTooltip);
